import { Dispatch } from "redux";
import format from "date-fns/format";
import startOfDay from "date-fns/startOfDay";
import endOfDay from "date-fns/endOfDay";
import {
    setDashboardSearchResults,
    setDashboardSearchResultsLoading
} from "@redux/actions/dashboard-actions";
import { RpServiceType } from "@idw-types/data/reference-data/generic";
import { getDashboardSummaryRequest } from "@requests/reports/get-dashboard-summary";
import { getDashboardVerificationCountsRequest } from "@requests/reports/get-dashboard-verification-counts";
import { getDashboardVerificationTransactionsRequest } from "@requests/reports/get-dashboard-verification-transactions";
import { getDashboardVerificationServicesRequest } from "@requests/reports/get-dashboard-verification-services";
import { SERVER_DATE_TIME_FORMAT } from "@constants/date-formats";
import { resources } from "@constants/hardcoded-strings";
import { handleResponse } from "@utils/promise-util";
import { formatDashboardVerificationServices } from "@formatters/dashboard-verification-services/dashboard-verification-service-formatter";
import { DashboardGroupByTypes } from "@enums/dashboard-group-by-types";

export const getDashboardDataReduxRequest = (dispatch: Dispatch) => async (
    companyId: string,
    startDate: Date,
    endDate: Date,
    groupBy: DashboardGroupByTypes,
    rpServiceTypes: Array<RpServiceType>
) => {
    dispatch(setDashboardSearchResultsLoading(true));

    const startDateString = format(groupBy === DashboardGroupByTypes.DATE_YEAR_MONTH_DAY_HOUR ? startDate : startOfDay(startDate), SERVER_DATE_TIME_FORMAT);
    const endDateString = format(groupBy === DashboardGroupByTypes.DATE_YEAR_MONTH_DAY_HOUR ? endDate : endOfDay(endDate), SERVER_DATE_TIME_FORMAT);
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    const [
        dashboardSummaryResponse,
        dashboardVerificationCountsResponse,
        dashboardVerificationTransactionsResponse,
        dashboardVerificationServicesResponse
    ] = await Promise.allSettled([
        getDashboardSummaryRequest(companyId, startDateString, endDateString, timeZone, SERVER_DATE_TIME_FORMAT),
        getDashboardVerificationCountsRequest(companyId, groupBy, startDateString, endDateString, timeZone, SERVER_DATE_TIME_FORMAT),
        getDashboardVerificationTransactionsRequest(companyId, timeZone),
        getDashboardVerificationServicesRequest(companyId, timeZone)
    ]);

    const dashboardSummaryData = handleResponse(
        dispatch,
        dashboardSummaryResponse,
        resources.requests.getDashboardSummary.error.title,
        resources.requests.getDashboardSummary.error.message,
    );

    const dashboardVerificationCountsData = handleResponse(
        dispatch,
        dashboardVerificationCountsResponse,
        resources.requests.getDashboardVerificationCounts.error.title,
        resources.requests.getDashboardVerificationCounts.error.message,
    );

    const dashboardVerificationTransactionsData = handleResponse(
        dispatch,
        dashboardVerificationTransactionsResponse,
        resources.requests.getDashboardVerificationTransactions.error.title,
        resources.requests.getDashboardVerificationTransactions.error.message,
    );

    const dashboardVerificationServicesData = handleResponse(
        dispatch,
        dashboardVerificationServicesResponse,
        resources.requests.getDashboardVerificationServices.error.title,
        resources.requests.getDashboardVerificationServices.error.message,
    );

    const formattedDashboardVerificationServices = dashboardVerificationServicesData?.verificationServices
        ? formatDashboardVerificationServices(
            dashboardVerificationServicesData.verificationServices,
            rpServiceTypes
        ) : null;

    dispatch(setDashboardSearchResults({
        summary: dashboardSummaryData,
        verificationCounts: dashboardVerificationCountsData?.verificationCounts || null,
        verificationServices: formattedDashboardVerificationServices,
        verificationTransactions: dashboardVerificationTransactionsData?.verificationTransactions || null
    }));
};